import { useEffect, useState } from "react"
import { Loader2, Plus, Trash2 } from "lucide-react"
import {
  CLIENTS_SOURCE_LABELS,
  LOCKED_STAGES,
  STAGES,
  type ClientsSource,
  type Settings,
  type Stage,
  type StageConfig,
} from "../../shared/domain"
import { Panel } from "@/components/common"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { apiErrorMessage } from "@/lib/api"
import { useApp } from "@/lib/app-context"

/**
 * Administração: o cliente define os valores (motivos, gravidades e prazos,
 * remetente, textos do portal e do RGPD); a app só dá os campos.
 */
export function SettingsPage() {
  const { api, settings, setSettings, toast } = useApp()
  const [draft, setDraft] = useState<Settings>(settings)
  const [saving, setSaving] = useState(false)
  useEffect(() => setDraft(settings), [settings])
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings)

  const patch = <K extends keyof Settings>(key: K, value: Settings[K]) => setDraft((d) => ({ ...d, [key]: value }))

  const save = async () => {
    setSaving(true)
    try {
      const saved = await api.saveSettings(draft)
      setSettings(saved)
      toast("success", "Definições guardadas")
    } catch (e) {
      toast("error", "Não foi possível guardar", apiErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4 pb-16">
      <div>
        <h2 className="text-base font-semibold">Administração</h2>
        <p className="text-xs text-muted-foreground">Motivos, resoluções, etapas, gravidades e prazos, clientes, emails, portal e RGPD</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Motivos de reclamação">
          <ul className="space-y-2">
            {draft.categories.map((c, i) => (
              <li key={i} className="flex gap-2">
                <Input
                  value={c}
                  onChange={(e) => patch("categories", draft.categories.map((x, j) => (j === i ? e.target.value : x)))}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remover"
                  onClick={() => patch("categories", draft.categories.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => patch("categories", [...draft.categories, ""])}>
            <Plus className="h-4 w-4" /> Motivo
          </Button>
        </Panel>

        <Panel title="Tipos de resolução">
          <ul className="space-y-2">
            {draft.resolutions.map((r, i) => (
              <li key={i} className="flex gap-2">
                <Input
                  value={r}
                  onChange={(e) => patch("resolutions", draft.resolutions.map((x, j) => (j === i ? e.target.value : x)))}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remover"
                  onClick={() => patch("resolutions", draft.resolutions.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => patch("resolutions", [...draft.resolutions, ""])}>
            <Plus className="h-4 w-4" /> Tipo de resolução
          </Button>
          <p className="mt-3 text-xs text-muted-foreground">
            O cliente escolhe um no portal («O que espera da nossa parte?») e a equipa escolhe o que deu ao encerrar — o painel compara os dois.
          </p>
        </Panel>

        <Panel title="Etapas do tratamento" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="pb-2 font-medium">Nome na ficha</th>
                  <th className="pb-2 font-medium">Nome para o cliente</th>
                  <th className="pb-2 text-center font-medium">Ativa</th>
                  <th className="pb-2 text-center font-medium">Texto obrigatório</th>
                </tr>
              </thead>
              <tbody>
                {STAGES.map((s) => (
                  <StageRow
                    key={s}
                    stage={s}
                    cfg={draft.stages[s]}
                    onChange={(cfg) => patch("stages", { ...draft.stages, [s]: cfg })}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Registo e Fecho estão sempre ativas. «Texto obrigatório» impede avançar sem preencher o texto da etapa (Ação corretiva aceita a
            correção imediata ou uma ação registada). O Fecho pede sempre o tipo de resolução e o texto para o cliente.
          </p>
        </Panel>

        <Panel title="Fonte de clientes">
          <div className="grid gap-2">
            {(Object.keys(CLIENTS_SOURCE_LABELS) as ClientsSource[]).map((src) => (
              <label key={src} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="clients-source"
                  checked={draft.clients.source === src}
                  onChange={() => patch("clients", { ...draft.clients, source: src })}
                />
                {CLIENTS_SOURCE_LABELS[src]}
              </label>
            ))}
            {draft.clients.source === "erp" ? (
              <div className="mt-1 grid gap-1.5">
                <Label>ERP (opcional)</Label>
                <Input
                  value={draft.clients.erp_provider}
                  placeholder="Todos — ou phc, odoo, moloni…"
                  onChange={(e) => patch("clients", { ...draft.clients, erp_provider: e.target.value })}
                />
              </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Define a lista de Clientes e quem recebe as campanhas de inquérito. Com «Excel», os clientes são os que importa em Clientes; com
              ERP ou CRM, vêm do Koortex e a importação fica escondida. A identificação no portal (por email) procura sempre em todos.
            </p>
          </div>
        </Panel>

        <Panel title="Gravidade e prazo de tratamento">
          <ul className="space-y-2">
            {draft.severities.map((s, i) => (
              <li key={i} className="grid grid-cols-[1fr_7rem_auto] items-center gap-2">
                <Input
                  value={s.label}
                  placeholder="Nome"
                  onChange={(e) => patch("severities", draft.severities.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                />
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={0}
                    max={365}
                    value={s.sla_days}
                    onChange={(e) =>
                      patch("severities", draft.severities.map((x, j) => (j === i ? { ...x, sla_days: Number(e.target.value) } : x)))
                    }
                  />
                  <span className="text-xs text-muted-foreground">dias</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remover"
                  onClick={() => patch("severities", draft.severities.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => patch("severities", [...draft.severities, { key: "", label: "", sla_days: 10 }])}
          >
            <Plus className="h-4 w-4" /> Gravidade
          </Button>
          <p className="mt-3 text-xs text-muted-foreground">O prazo conta a partir do registo e define a data limite da reclamação.</p>
        </Panel>

        <Panel title="Emails">
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Nome do remetente</Label>
              <Input value={draft.sender.name} placeholder="Qualidade · Empresa" onChange={(e) => patch("sender", { ...draft.sender, name: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Responder para</Label>
              <Input
                type="email"
                value={draft.sender.reply_to}
                placeholder="qualidade@empresa.pt"
                onChange={(e) => patch("sender", { ...draft.sender, reply_to: e.target.value })}
              />
            </div>
            <p className="text-xs text-muted-foreground">Os emails saem pelo servidor de email configurado no Koortex.</p>
          </div>
        </Panel>

        <Panel title="Objetivos e alertas">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Tempo de fecho (dias)</Label>
              <Input
                type="number"
                min={1}
                value={draft.goals.close_days}
                onChange={(e) => patch("goals", { ...draft.goals, close_days: Number(e.target.value) })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Satisfação média (1–5)</Label>
              <Input
                type="number"
                step="0.1"
                min={1}
                max={5}
                value={draft.goals.satisfaction_avg}
                onChange={(e) => patch("goals", { ...draft.goals, satisfaction_avg: Number(e.target.value) })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Avaliação baixa: NPS até</Label>
              <Input
                type="number"
                min={0}
                max={10}
                value={draft.surveys.low_score_nps}
                onChange={(e) => patch("surveys", { ...draft.surveys, low_score_nps: Number(e.target.value) })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>ou média até</Label>
              <Input
                type="number"
                step="0.1"
                min={1}
                max={5}
                value={draft.surveys.low_score_avg}
                onChange={(e) => patch("surveys", { ...draft.surveys, low_score_avg: Number(e.target.value) })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Inquéritos automáticos: mínimo de dias entre inquéritos ao mesmo email</Label>
              <Input
                type="number"
                min={0}
                max={365}
                value={draft.surveys.min_days_between}
                onChange={(e) => patch("surveys", { ...draft.surveys, min_days_between: Number(e.target.value) })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Emails que nunca recebem inquéritos (um por linha)</Label>
              <Textarea
                rows={3}
                value={draft.surveys.excluded_emails.join("\n")}
                onChange={(e) =>
                  patch("surveys", { ...draft.surveys, excluded_emails: e.target.value.split(/\r?\n/).map((x) => x.trim()).filter(Boolean) })
                }
              />
            </div>
          </div>
        </Panel>

        <Panel title="Portal do cliente">
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Texto de abertura</Label>
              <Textarea rows={3} value={draft.portal.intro_text} onChange={(e) => patch("portal", { ...draft.portal, intro_text: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Mensagem depois de enviar</Label>
              <Textarea rows={2} value={draft.portal.thanks_text} onChange={(e) => patch("portal", { ...draft.portal, thanks_text: e.target.value })} />
            </div>
          </div>
        </Panel>

        <Panel title="RGPD">
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Texto de consentimento</Label>
              <Textarea rows={4} value={draft.rgpd.consent_text} onChange={(e) => patch("rgpd", { ...draft.rgpd, consent_text: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Conservar reclamações e respostas durante (meses)</Label>
              <Input
                type="number"
                min={1}
                max={240}
                className="w-32"
                value={draft.rgpd.retention_months}
                onChange={(e) => patch("rgpd", { ...draft.rgpd, retention_months: Number(e.target.value) })}
              />
            </div>
          </div>
        </Panel>
      </div>

      {dirty ? (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border/60 bg-card/90 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-6xl justify-end gap-2">
            <Button variant="outline" onClick={() => setDraft(settings)}>
              Descartar
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar alterações
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function StageRow({ stage, cfg, onChange }: { stage: Stage; cfg: StageConfig; onChange(cfg: StageConfig): void }) {
  const locked = LOCKED_STAGES.includes(stage)
  return (
    <tr className="border-t border-border/50">
      <td className="py-2 pr-2">
        <Input value={cfg.label} onChange={(e) => onChange({ ...cfg, label: e.target.value })} />
      </td>
      <td className="py-2 pr-2">
        <Input value={cfg.public_label} onChange={(e) => onChange({ ...cfg, public_label: e.target.value })} />
      </td>
      <td className="py-2 text-center">
        <Switch checked={locked || cfg.enabled} disabled={locked} onCheckedChange={(v) => onChange({ ...cfg, enabled: v })} />
      </td>
      <td className="py-2 text-center">
        {stage === "registo" || stage === "fecho" || stage === "eficacia" ? (
          <span className="text-xs text-muted-foreground">{stage === "fecho" ? "sempre" : "—"}</span>
        ) : (
          <Switch checked={cfg.required} disabled={!cfg.enabled} onCheckedChange={(v) => onChange({ ...cfg, required: v })} />
        )}
      </td>
    </tr>
  )
}
